/**
 * Live Table Message Transformer
 *
 * Converts the new live-table WebSocket message format into the
 * existing format that all 28 game components already consume.
 *
 * New format (from provider):
 *   { mid, ft, card, sub[], gtype, ... }
 *
 * Old format (expected by game components):
 *   { marketId, autoTime, cards[][], oddDetailsDTOS[] }
 */

/**
 * Card grouping rules per gmid.
 * Defines how many cards belong in each group.
 * e.g., [3, 3] means 2 groups of 3 cards each (TeenPatti: Player A, Player B).
 *
 * Games not listed fall back to a single flat group.
 */
const CARD_GROUPS = {
  dt20: [1, 1],
  dt202: [1, 1],
  dt6: [1, 1],
  dtl20: [1, 1, 1],
  teen20: { interleave: [3, 3] },
  teen9: { interleave: [3, 3, 3] },
  teen8: { interleave: [3, 3, 3, 3, 3, 3, 3, 3] },
  teen: { interleave: [3, 3] },
  poker20: [2, 2, 5],
  poker9: [2, 2, 2, 2, 2, 2, 5],
  poker1day: [5, 5],
  card32: [2, 2, 2, 2],
  card32eu: [2, 2, 2, 2],
  baccarat: { interleave: [3, 3] },
  baccarat2: { interleave: [3, 3] },
  ab20: "andar-bahar",
  abj: "andar-bahar-joker",
  lucky7: [1],
  lucky7eu: [1],
  queen: [1, 1, 1, 1],
  war: [1, 1, 1, 1, 1, 1, 1],
  cmeter: [3, 3],
  race20: [1, 1, 1, 1],
  aaa: [1, 1, 1],
  btable: [],
  worli: [1, 1, 1],
  worli3: [1, 1, 1],
  cmatch20: [],
  cricketv3: [],
  superover: [],
};

/**
 * Convert a single card token from the new provider format to the
 * old suit-prefix format used by getCardUrl().
 *
 * New format examples:
 *   "2HH" → "H2"    (value + doubled suit)
 *   "KDD" → "DK"
 *   "ADD" → "DA"     (Ace of Diamonds)
 *   "10SS" → "S10"
 *   "1" → "1"        (plain number — already handled by getCardUrl)
 *
 * @param {string} token — raw card token from provider
 * @returns {string} — card code in old format
 */
export const convertCardToken = (token) => {
  if (!token) return "0";

  const t = token.trim();
  if (!t) return "0";

  // Already in old format (single letter suit prefix like "SA", "H2", "DK")
  if (/^[SHDC]\d/.test(t) || /^[SHDC][AJQK]$/i.test(t)) return t;

  // Plain number (Ace=1, 2-13) — keep as-is
  if (/^\d+$/.test(t)) return t;

  // New provider format: {value}{suit}{suit} e.g. "2HH", "KDD", "ADD", "10SS"
  const match = t.match(/^(.+?)(HH|CC|DD|SS)$/i);
  if (match) {
    const value = match[1];
    const suit = match[2][0].toUpperCase();
    return suit + value;
  }

  // Single suit letter + value without doubling (e.g. "2H", "KC")
  const singleMatch = t.match(/^(.+?)([HCDS])$/i);
  if (singleMatch) {
    const value = singleMatch[1];
    const suit = singleMatch[2].toUpperCase();
    return suit + value;
  }

  // Unknown format — return as-is, getCardUrl will show card back
  return t;
};

/**
 * Convert a comma-separated card string into an array of converted tokens.
 */
const convertCardString = (str) => {
  if (!str || typeof str !== "string") return [];
  return str.split(",").map((t) => t.trim()).filter(Boolean).map(convertCardToken);
};

/**
 * Parse the raw card string from the provider into the nested array
 * format expected by game components.
 *
 * @param {string} cardString — e.g. "ADD,2HH,6CC,KHH,9DD,1"
 * @param {string} gmid — game identifier for grouping rules
 * @param {Object} raw — full raw message (for games needing extra fields like aall/ball)
 * @returns {Array<Array<string>>} — e.g. [["DA","H2","C6"],["HK","D9","1"]]
 */
const parseCards = (cardString, gmid, raw) => {
  const groupRule = CARD_GROUPS[gmid];

  // Andar Bahar: use aall/ball fields for proper Andar vs Bahar split
  if (groupRule === "andar-bahar" && raw?.aall != null && raw?.ball != null) {
    const andar = convertCardString(raw.aall);
    const bahar = convertCardString(raw.ball);
    return [andar, bahar];
  }
  if (groupRule === "andar-bahar-joker" && raw?.aall != null && raw?.ball != null) {
    const allTokens = convertCardString(cardString);
    const andar = convertCardString(raw.aall);
    const bahar = convertCardString(raw.ball);
    // First card in the full card string is the joker/house card
    const joker = allTokens.length > 0 ? [allTokens[0]] : [];
    return [joker, andar, bahar];
  }

  if (!cardString || typeof cardString !== "string") return [];

  const tokens = cardString.split(",").map((t) => t.trim()).filter(Boolean);
  if (tokens.length === 0) return [];

  const converted = tokens.map(convertCardToken);

  // Interleaved dealing (round-robin): cards arrive as A,B,A,B,A,B
  // and need to be de-interleaved into [A,A,A], [B,B,B]
  if (groupRule && typeof groupRule === "object" && Array.isArray(groupRule.interleave)) {
    const sizes = groupRule.interleave;
    const numPlayers = sizes.length;
    const groups = sizes.map(() => []);
    for (let i = 0; i < converted.length; i++) {
      const playerIdx = i % numPlayers;
      if (groups[playerIdx].length < sizes[playerIdx]) {
        groups[playerIdx].push(converted[i]);
      }
    }
    return groups;
  }

  const groupSizes = groupRule;

  // No grouping rules — wrap everything as a single group
  if (!Array.isArray(groupSizes) || groupSizes.length === 0) {
    return converted.length > 0 ? [converted] : [];
  }

  // Split into groups based on defined sizes
  const groups = [];
  let offset = 0;
  for (const size of groupSizes) {
    if (offset >= converted.length) break;
    groups.push(converted.slice(offset, offset + size));
    offset += size;
  }

  // Any remaining cards go into a final group
  if (offset < converted.length) {
    groups.push(converted.slice(offset));
  }

  return groups;
};

/**
 * Worli/Matka runner name mapping from sr (sort rank) to display name.
 * sr 1-9 = digits "1"-"9", sr 10 = "0", sr 11-14 = group bets.
 */
const WORLI_SR_NAMES = {
  1: "1", 2: "2", 3: "3", 4: "4", 5: "5",
  6: "6", 7: "7", 8: "8", 9: "9", 10: "0",
  11: "Line 1", 12: "Line 2", 13: "Odd", 14: "Even",
};

/**
 * Derive runner name for games that don't send the `nat` field.
 * Worli uses sr (sort rank) to identify selections.
 */
const deriveRunnerName = (sub) => {
  if (sub.nat) return sub.nat;
  if (sub.subtype === "worli" && sub.sr != null) {
    return WORLI_SR_NAMES[sub.sr] || String(sub.sr);
  }
  return "";
};

/**
 * Convert a single runner/selection from new format to old format.
 *
 * New:  { nat, sid, b, l, bs, ls, gstatus, min, max, etype, subtype, visible, ... }
 * Old:  { runnerName, selectionId, back1, lay1, status, ... }
 */
const transformRunner = (sub) => ({
  runnerName: deriveRunnerName(sub),
  selectionId: sub.sid,
  back1: sub.b != null ? String(sub.b) : "0",
  lay1: sub.l != null ? String(sub.l) : "0",
  backSize: sub.bs || 0,
  laySize: sub.ls || 0,
  status: sub.gstatus === "OPEN" ? "ACTIVE" : sub.gstatus || "SUSPENDED",
  min: sub.min || 0,
  max: sub.max || 0,
  etype: sub.etype || "match",
  subtype: sub.subtype || "",
  visible: sub.visible,
  gval: sub.gval,
  sr: sub.sr,
});

/**
 * Convert a t1/t2 section entry (used by Super Over, Cricket Match, etc.)
 * into the runner format expected by game components.
 *
 * Section format:
 *   { sid, nat, gstatus, min, max, odds: [{ otype, odds, size }, ...] }
 *
 * Output format:
 *   { runnerName, selectionId, back1, lay1, backSize1, laySize1, status, min, max }
 */
const transformSectionRunner = (section) => {
  const backOdd = section.odds?.find((o) => o.otype === "back");
  const layOdd = section.odds?.find((o) => o.otype === "lay");
  return {
    runnerName: section.nat || "",
    selectionId: section.sid || section.psid,
    back1: backOdd ? String(backOdd.odds) : "0",
    lay1: layOdd ? String(layOdd.odds) : "0",
    backSize1: backOdd?.size || 0,
    laySize1: layOdd?.size || 0,
    status: section.gstatus || "SUSPENDED",
    min: section.min || 0,
    max: section.max || 0,
  };
};

/**
 * Transform t1/t2 format messages (used by Super Over, Cricket Match, etc.)
 *
 * t1 contains game metadata (gmid, ft, card, gtype).
 * t2 is an array of markets identified by dtype:
 *   dtype 2  → Bookmaker  → oddDetailsDTOS
 *   dtype 6  → Fancy      → fancyOddDetailsDTOS
 *   dtype 7  → Fancy1     → fancy2OddDetailsDTOS
 */
const transformT1T2Message = (raw, gmid) => {
  const t1 = raw.t1 || {};
  const t2 = Array.isArray(raw.t2) ? raw.t2 : [];

  const bookmaker = t2.find((m) => m.dtype === 2);
  const fancy = t2.find((m) => m.dtype === 6);
  const fancy1 = t2.find((m) => m.dtype === 7);

  // Only produce arrays for markets present in this message.
  // Missing markets return undefined so the caller can preserve previous values.
  const oddDetailsDTOS = bookmaker ? (bookmaker.section || []).map(transformSectionRunner) : undefined;
  const fancyOddDetailsDTOS = fancy ? (fancy.section || []).map(transformSectionRunner) : undefined;
  const fancy2OddDetailsDTOS = fancy1 ? (fancy1.section || []).map(transformSectionRunner) : undefined;

  const cardString = typeof t1.card === "string"
    ? t1.card.replace(/\|/g, ",")
    : "";

  return {
    marketId: t1.gmid != null ? String(t1.gmid) : "",
    autoTime: t1.ft != null ? t1.ft : undefined,
    cards: parseCards(cardString, gmid, raw),
    oddDetailsDTOS,
    fancyOddDetailsDTOS,
    fancy2OddDetailsDTOS,
    gtype: t1.gtype || "",
    _raw: raw,
  };
};

/**
 * Transform a full live-table WebSocket message into the format
 * consumed by all game components via Redux.
 *
 * @param {Object} raw — raw message from provider
 * @param {string} gmid — game identifier (for card grouping)
 * @returns {Object} — transformed message in the old format
 */
export const transformLiveTableMessage = (raw, gmid) => {
  if (!raw) return null;

  // t1/t2 format used by Super Over, Cricket Match, etc.
  if (raw.t1 && raw.t2) {
    return transformT1T2Message(raw, gmid);
  }

  // Only filter by `visible` when the field is actively used (mix of 0 and 1).
  // Some games (e.g. poker20) send all runners with visible:0 — treat as "no filtering".
  const subs = Array.isArray(raw.sub) ? raw.sub : [];
  const hasAnyVisible = subs.some((s) => s.visible === 1);
  const oddDetailsDTOS = subs
    .filter((s) => !hasAnyVisible || s.visible === undefined || s.visible === 1)
    .map(transformRunner);

  return {
    // Core fields used by every game component
    marketId: raw.mid != null ? String(raw.mid) : "",
    autoTime: raw.ft != null ? raw.ft : undefined,
    cards: parseCards(raw.card, gmid, raw),
    oddDetailsDTOS,

    // Pass through additional fields for future use
    gtype: raw.gtype || "",
    remark: raw.remark || "",
    grp: raw.grp,
    lvl: raw.lvl,
    lt: raw.lt,
    vv: raw.vv,

    // Keep raw data accessible
    _raw: raw,
  };
};
